import { AnchorButton, Tooltip } from '@blueprintjs/core';
import {
  debt_register_hooks,
  getTermsAndConditions,
  lock_hooks,
  useApplicationDetail,
  useApplicationStatusUtils,
  useCloneApplication,
  useCreateConsent,
  usePerson,
} from '@axo/shared/data-access/hooks';
import {
  event_log,
  loan_application,
  Product,
} from '@axo/shared/data-access/types';
import { MouseEvent, useContext } from 'react';
import { ApplicationContext } from '../../../store/context';
import { useNavigate } from 'react-router-dom';
import { useConfirmDialog } from '../../../hooks/utils/useConfirmDialog';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';
import { EventCodeACE } from '../../EventLogWrapper/event-code';
import { getApplicationDataStore } from '../../../store/applicationDataStore';
import {
  DialogFormState,
  DialogStateFormMode,
} from '../../ConfirmDialogProvider/ConfirmDialogProvider';
import { MarketCountryCode } from '@axo/shared/types';
import { getBrandForGeneralConsent } from '@axo/config/util/get-brand-for-general-consent';

export function CloneApplicationButton({
  applicationID,
}: {
  applicationID: string;
}) {
  const { dispatch } = useContext(ApplicationContext);
  const navigate = useNavigate();
  const cloneApplication = useCloneApplication();
  const app = useApplicationDetail(applicationID);
  const { data: personData } = usePerson(app?.data?.PersonID);
  const { mutateAsync: updateDebtRegister } =
    debt_register_hooks.useUpdateDebtRegistryData();
  const { mutateAsync: createConsent } = useCreateConsent();

  const { isFinalApplicationStatus } = useApplicationStatusUtils();
  const acquireLock = lock_hooks.useLockApplication();
  const { confirm } = useConfirmDialog();
  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    app.data?.MarketCountry
  ).data;

  const openApplication = (application: loan_application.LoanApplication) => {
    beginEdit(application);
    navigate(`/application/${application.ID}`);
  };

  const beginEdit = async (
    clonedApplication: loan_application.LoanApplication
  ) => {
    dispatch({
      type: 'Open application',
      scope: {
        parentType: 'navigation',
        id: clonedApplication.ID,
      },
      payload: {
        humanReadableID:
          clonedApplication.HumanReadableID || clonedApplication.ID,
        marketCountry: clonedApplication.MarketCountry,
      },
    });

    await acquireLock.mutateAsync({
      marketCountry: clonedApplication.MarketCountry,
      applicationID: clonedApplication.ID,
    });

    const { setIsEditing } = getApplicationDataStore(
      clonedApplication.ID
    ).getState();
    setIsEditing(true);
  };

  const isFinal = app.data?.Status && isFinalApplicationStatus(app.data.Status);
  const appTag: event_log.Resource = {
    Type: KnownResourceType.LoanApplication,
    ID: applicationID,
  };
  const log = useEventLogger(appTag);
  const handleClone = async (e: MouseEvent<HTMLElement>) => {
    const formMode = [
      ...([MarketCountryCode.Norway, MarketCountryCode.Finland].includes(
        app.data?.MarketCountry as MarketCountryCode,
      )
        ? [DialogStateFormMode.PRODUCT_TYPE]
        : []),
      ...(app.data?.CoApplicantCustomerID && app.data?.CoApplicantPersonID
        ? [DialogStateFormMode.CO_APPLICANT]
        : []),
    ];
    const formProps = {
      product: app.data?.Product,
    };
    e.currentTarget.blur();
    confirm({
      message: 'Are you sure you want to clone this application?',
      confirmText: 'Clone',
      cancelText: 'Cancel',
      formMode,
      formProps,
      confirmCallback: async (dialogFormData?: DialogFormState) => {
        const clonedApplicationPatches: Partial<loan_application.LoanApplication> =
          {};

        if (!dialogFormData?.coApplicant.cloneCoApplicant) {
          clonedApplicationPatches.CoApplicantCustomerID = null;
          clonedApplicationPatches.CoApplicantPersonID = null;
        }
        clonedApplicationPatches.Product = dialogFormData?.product
          .selected as Product;

        if (app.data?.Debts) {
          clonedApplicationPatches.Debts = app.data.Debts.map((debt) => ({
            ...debt,
            RefinanceAmount: 0,
          }));
        }
        if (app.data?.CoDebts) {
          clonedApplicationPatches.CoDebts = app.data.CoDebts.map((debt) => ({
            ...debt,
            RefinanceAmount: 0,
          }));
        }
        clonedApplicationPatches.TotalRefinancingAmount = 0;

        const application = await cloneApplication.mutateAsync({
          loanApplicationID: applicationID,
          data: {
            ...clonedApplicationPatches,
            Channels: [
              {
                CampaignName: 'ace',
                CampaignMedium: 'ace',
                CampaignSource: 'ace',
              },
            ],
          },
        });

        if (!application.Meta?.Brand) {
          throw new Error('Cloned application is missing a Brand');
        }

        const generalConsentBrand = getBrandForGeneralConsent(
          application.Meta.Brand
        );
        const termsAndConditions = await getTermsAndConditions(
          application.Meta.Brand
        );
        const generalTermsVersion = termsAndConditions.terms.find(
          (terms) => terms.terms.tc_type === 'GENERAL_TERMS'
        )?.version;
        if (!generalTermsVersion)
          throw new Error('Failed to fetch general terms and conditons');

        await createConsent({
          Country: application.MarketCountry,
          OriginBrandID: generalConsentBrand,
          ExternalVersion: generalTermsVersion,
          CustomerID: application.CustomerID,
          Lang: 'N/A',
          OriginAction: 'ace-clone-application',
          ConfigType: 'GENERAL_TERMS',
        });

        if (
          application.MarketCountry === MarketCountryCode.Norway &&
          personData?.SocialSecurityNumber
        ) {
          try {
            await updateDebtRegister({
              applicationID: application.ID,
              SSN: personData.SocialSecurityNumber,
            });
          } catch (error) {
            console.error(
              'Failed to update debt register data for cloned application',
              error
            );
          }
        }

        log(EventCodeACE.CloneApplication);
        openApplication(application);
      },
    });
  };

  function canClone() {
    if (!hasLock) {
      return {
        disabled: true,
        text: 'You can only clone the application when you have locked it.',
      };
    }
    if (!isFinal) {
      return {
        disabled: true,
        text: 'Applications can only be cloned when in a final state.',
      };
    }

    return {
      disabled: false,
      text: '',
    };
  }

  return (
    <Tooltip
      disabled={!canClone().disabled}
      placement="top"
      content={<span>{canClone().text}</span>}
    >
      <AnchorButton
        intent="none"
        icon="duplicate"
        disabled={canClone().disabled}
        onClick={handleClone}
        loading={cloneApplication.isLoading}
      >
        Clone application
      </AnchorButton>
    </Tooltip>
  );
}
